import React, { useEffect, useMemo, useRef } from "react";
import { Empty } from "antd";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const COLORS = [
  "rgba(59, 130, 246, 0.85)",
  "rgba(234, 179, 8, 0.85)",
  "rgba(34, 197, 94, 0.85)",
];

function formatBarLabel(n) {
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "0";
  return Math.abs(n) >= 1e9
    ? `${(n / 1e9).toFixed(2)}B`
    : Math.abs(n) >= 1e6
      ? `${(n / 1e6).toFixed(2)}M`
      : Math.abs(n) >= 1e3
        ? n.toLocaleString("en-US")
        : String(Math.round(n));
}

export default function StatisticReportBarChart({
  labels,
  datasets,
  height = 360,
  legendPosition = "top",
  showDataLabels = false,
}) {
  const chartRef = useRef(null);

  const barTopLabelsPlugin = useMemo(
    () => ({
      id: "barTopLabelsOnHover",
      afterDatasetsDraw(chart) {
        if (!showDataLabels) return;
        const active = chart.getActiveElements();
        if (!active?.length) return;
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        ctx.save();
        ctx.font = "600 11px system-ui, sans-serif";
        ctx.fillStyle = "#1f2937";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        const seen = new Set();
        for (const el of active) {
          const key = `${el.datasetIndex}:${el.index}`;
          if (seen.has(key)) continue;
          seen.add(key);
          const dataset = chart.data.datasets[el.datasetIndex];
          const meta = chart.getDatasetMeta(el.datasetIndex);
          const bar = meta.data[el.index];
          if (!bar) continue;
          const v = dataset.data[el.index];
          const n = typeof v === "number" ? v : Number(v);
          if (!Number.isFinite(n)) continue;
          const text = formatBarLabel(n);
          if (!text) continue;
          const y = Math.max(chartArea.top + 2, bar.y - 4);
          ctx.fillText(text, bar.x, y);
        }
        ctx.restore();
      },
    }),
    [showDataLabels]
  );

  const plugins = useMemo(() => {
    return showDataLabels ? [barTopLabelsPlugin] : [];
  }, [showDataLabels, barTopLabelsPlugin]);

  // Tab ẩn → hiện: canvas đôi khi khởi tạo với kích thước 0; cần resize sau layout.
  useEffect(() => {
    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        chartRef.current?.resize?.();
      });
    });
    return () => {
      cancelled = true;
    };
  }, [labels, datasets]);

  if (!labels?.length || !datasets?.length) {
    return (
      <Empty
        description="Chưa có dữ liệu biểu đồ. Vui lòng tìm kiếm trước."
        style={{ margin: "48px 0" }}
      />
    );
  }

  const data = {
    labels,
    datasets: datasets.map((ds, i) => ({
      ...ds,
      backgroundColor: ds.backgroundColor ?? COLORS[i % COLORS.length],
      borderRadius: 4,
      borderSkipped: false,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: legendPosition,
        labels: { boxWidth: 14, padding: 16 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const v = ctx.parsed?.y;
            const n = typeof v === "number" ? v : Number(v);
            return `${ctx.dataset.label}: ${
              Number.isFinite(n) ? n.toLocaleString("vi-VN") : v
            }`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxRotation: 45, minRotation: 0 },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.06)" },
        ticks: {
          callback: (value) => Number(value).toLocaleString("vi-VN"),
        },
      },
    },
  };

  return (
    <div style={{ width: "100%", height, minHeight: 280 }}>
      <Bar ref={chartRef} data={data} options={options} plugins={plugins} />
    </div>
  );
}
